import { retrieveRawInitData } from '@telegram-apps/sdk-react';

import { logger } from '@app/logger';

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * `retrieveRawInitData` has two distinct failure modes and they need
 * different handling:
 *
 *  - it RETURNS empty while launch params have not arrived yet — transient,
 *    worth retrying;
 *  - it THROWS when the params cannot be resolved at all. That is
 *    deterministic for the whole page load (they come from `location` and
 *    `sessionStorage`), so retrying it just burns the full 10 x 100ms budget
 *    on the auth path — for precisely the users whose Telegram environment is
 *    already broken. Bail out immediately instead.
 *
 * Either way callers receive `null`, which they all already handle
 * (tokenService, appInstanceService, AnalyticsService) — letting the throw
 * escape would reject them mid-flight.
 */
let hasReportedInitDataFailure = false;

const readRawInitData = (): { threw: boolean; value: string | null } => {
  try {
    return { threw: false, value: retrieveRawInitData() || null };
  } catch (initDataError) {
    // Report once per page load: this is otherwise the single place in the
    // guard work that swallows without a trace, and a NEW throw reason would
    // silently degrade every caller to the unauthenticated path.
    if (!hasReportedInitDataFailure) {
      hasReportedInitDataFailure = true;
      try {
        logger.captureException(initDataError, {
          tags: { tmaCall: 'retrieveRawInitData' },
        });
      } catch {
        // Reporting must never break the auth path.
      }
    }

    return { threw: true, value: null };
  }
};

export const waitForTmaInitData = async (
  maxAttempts = 10,
  delayMs = 100,
): Promise<string | null> => {
  for (let i = 0; i < maxAttempts; i++) {
    const { threw, value } = readRawInitData();

    if (value) {
      return value;
    }

    // Deterministic failure — further attempts cannot succeed.
    if (threw) {
      return null;
    }

    if (i < maxAttempts - 1) {
      await delay(delayMs);
    }
  }

  return null;
};

export const hasTmaInitData = () => Boolean(readRawInitData().value);
